import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { NavLink, useNavigate } from "react-router-dom";
import { IPageParams } from "../../api/IApi";
import { getErrorFromCatch } from "../../api/api";
import { userApi } from "../../api/user.api";
import { useConfirm } from "../../hooks/useConfirm";
import { useTypedDispatch } from "../../hooks/useTypedDispatch";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { useUser } from "../../hooks/useUser";
import { ROLES } from "../../model/IRole";
import { msg } from "../../msg";
import { getUserlistPageParams, setUserlistPageParams } from "../../store/userSlice";
import parseISOToString from "../../utils/parseISOToString";
import { IFilterState } from "../common/filter/Filter";
import Table, { ITableTh } from "../common/table/Table";
import UserActionMenu from "./UserActionMenu";
import UserSearch, { IUserSearchValue } from "./UserSearch";
import Truncate from "../common/Truncate";
import Switch from "../form/Switch";
import { icons } from "../../icons/IconSet";
import { IUser } from "../../model/IUser";
import { getCurrentUser } from "../../store/authSlice";
import { useAuth } from "../../hooks/useAuth";

export type TUsersFilterFields =
  | 'email'
  | 'id'
  | 'phone'
  | 'name';

export type TUsersFilterState = IFilterState<Record<TUsersFilterFields, string>>;

const UserList: FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const dispatch = useTypedDispatch();
  const { editOptionsUserInfo } = useUser();

  // -- Page parameters and filter states
  // const [pageParams, setPageParams] = useState<IPageParams>({
  //   page: 0,
  //   size: CONFIG.user.usersPerPage,
  //   q: '',
  //   sort: '',
  //   sortDir: 'asc',
  // });
  const pageParams = useTypedSelector(getUserlistPageParams());
  const [isPending, setIsPending] = useState<boolean>(() => false);
  const currentUser = useTypedSelector(getCurrentUser());
  const { setMyself } = useAuth();

  const [filterState, setFilterState] = useState<IFilterState<Record<TUsersFilterFields, string>>>({
    id: '',
    email: '',
    phone: '',
    name: '',
  });

  // -- Load user list
  const { data: users, isFetching, isError, error } = userApi.useGetUserListQuery({
    ...pageParams,
    ...filterState,
  });

  // -- Table header
  const thead: ITableTh[] = [
    { title: 'Email', sortCol: 'email' },
    { title: 'Assignable', align: 'center' },
    { title: 'Searchable on sales', align: 'center' },
    { title: 'Searchable on cs', align: 'center' },
    { title: 'Name', sortCol: 'name' },
    { title: 'Phone', sortCol: 'phone' },
    { title: 'Enabled', sortCol: 'active', align: 'center' },
    { title: 'Roles' },
    { title: 'Created', sortCol: 'registered' },
    { title: '' },
  ];

  // -- Api Error handler
  useEffect(() => {
    isError && error && toast.error(`${msg('user-list')} ${getErrorFromCatch(error)}`);
  }, [isError]);

  // -- Apply a new search and jump back to the first page
  const handleSearch = ({ email, name }: IUserSearchValue) => {
    setFilterState((prevState) => ({ ...prevState, email, name }));
    dispatch(setUserlistPageParams({ ...pageParams, page: 0 }));
  };

  const handleEditOption = async (id: IUser['id'], isAllowedForTickets?: boolean, isSearchableSales?: boolean, isSearchableTickets?: boolean) => {
    setIsPending(() => true);

    const updatedUser = await editOptionsUserInfo(id, isAllowedForTickets, isSearchableSales, isSearchableTickets);
    if (!updatedUser?.id) {
      setIsPending(() => false);
      return;
    }

    currentUser?.id === updatedUser?.id && (await setMyself());
    toast.success(msg('user-edited'));
    setIsPending(() => false);
  }

  return (
    <Table
      size="small"
      options={[
        <UserSearch
          email={filterState.email}
          name={filterState.name}
          onChange={handleSearch}
        />,
      ]}
      isPending={isFetching}
      pageParams={pageParams}
      pagable={users}
      updatePageParams={(newParams: Partial<IPageParams>) =>
        dispatch(setUserlistPageParams({
          ...pageParams,
          ...newParams
        }))
        // setPageParams((prevState) => ({
        //   ...prevState,
        //   ...newParams
        // })) 
      }
      thead={thead}
      tbody={!users?.content?.length
        ? undefined
        : users.content.map(({ id, name, email, phone, active, userRole, isAllowedForTickets, isSearchableSales, isSearchableTickets, registered }) => ({
          onClick: () => navigate(`/users/${id}`),
          className: active
            ? ''
            : 'opacity-40',
          data: [
            <NavLink to={`${id}`}>{email}</NavLink>,
            <Switch
              isNoLabelArea
              size="tiny"
              name="isAllowedForTickets"
              value={!!isAllowedForTickets}
              handleToggle={() => handleEditOption(id, !!!isAllowedForTickets, isSearchableSales, isSearchableTickets)}
            />,
            <Switch
              isNoLabelArea
              size="tiny"
              name="isSearchableSales"
              value={!!isSearchableSales}
              handleToggle={() => handleEditOption(id, isAllowedForTickets, !!!isSearchableSales, isSearchableTickets)}
            />,
            <Switch
              isNoLabelArea
              size="tiny"
              name="isSearchableTickets"
              value={!!isSearchableTickets}
              handleToggle={() => handleEditOption(id, isAllowedForTickets, isSearchableSales, !!!isSearchableTickets)}
            />,
            <Truncate title={name}>
              {name || ' '}
            </Truncate>,

            <Truncate title={phone}>
              {phone || ' '}
            </Truncate>,

            active
              ? icons('check', { width: '18px' })
              : icons('x', { width: '18px' })
            ,
            <span className="whitespace-nowrap">
              {(userRole || [])?.map(({ role }) => ROLES?.[role]).join(', ')}
            </span>,

            <Truncate title={parseISOToString(registered || '')}>
              {parseISOToString(registered || '')}
            </Truncate>,

            <UserActionMenu userId={id} userEmail={email} />
          ]
        }))
      }
    />
  );
};

export default UserList;